import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { formatMoney } from 'common'
import { Formik } from 'formik'
import * as Yup from 'yup'

import {
  BenefitDesignMaxAllowances,
  getBenefitDesignMaxAllowances,
} from 'core/application/adminDashboard/benefitDetails/design/qsehra/productClass/getBenefitDesignMaxAllowances'
import {
  QsehraFamilyAllowanceRequest,
  SaveAllowancesState,
} from 'core/application/adminDashboard/benefitDetails/design/qsehra/productClass/getProductClassDesignWizardState'
import setProductClassAllowances, {
  SetProductClassAllowanceRequest,
} from 'core/application/adminDashboard/benefitDetails/design/qsehra/productClass/setProductClassAllowance'
import { FamilyStatus } from 'core/domain/companies/familyStatus'

import CheckboxV2 from 'common/components/dataEntry/formik/CheckboxV2'

import { Col, DefaultInput, Icon, Radio, Row, Skeleton } from 'common/components'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'

import { BaseComponentProps } from '../../../../../common/productClassWizardModal/Common'

import './setAllowances.less'

const enum AllowanceTypes {
  SameAllowance = 0,
  DifferentAllowance = 1,
}

type FormikValues = {
  allowanceOptionId: AllowanceTypes
  offerMaxAllowance: boolean
  sameAllowanceAmount: number
  sameAllowanceMax: number
  sameAllowanceNext: number
  singleAmount: number
  singleMax: number
  singleNext: number
  singleWithDependentsAmount: number
  singleWithDependentsMax: number
  singleWithDependentsNext: number
  marriedAmount: number
  marriedMax: number
  marriedNext: number
  marriedWithDependentsAmount: number
  marriedWithDependentsMax: number
  marriedWithDependentsNext: number
}

type InputMappingObject = {
  status: FamilyStatus
  icon: string
  label: string
  name: string
  placeholder: string
  nextAmount: number
}

export type SetAllowancesProps = {
  productClassId: string
  familyAllowances: QsehraFamilyAllowanceRequest[]
  nextFamilyAllowances: QsehraFamilyAllowanceRequest[]
  offerSameAllowance: boolean
  offerMaxAllowance: boolean
  saveAllowancesState: SaveAllowancesState
  isOfferSameAllowanceEditable: boolean
}

const allowancesOptions = [
  {
    id: AllowanceTypes.SameAllowance,
    title: 'Give all employees the same allowance',
  },
  {
    id: AllowanceTypes.DifferentAllowance,
    title: 'Give a higher allowance to employees who are married and/or have other dependents.',
  },
]

const getStateAmount = (state: SetAllowancesProps, status: FamilyStatus) =>
  state.familyAllowances.find((x) => x.familyStatusId === status)?.amount

const getMaxAmount = (maxAllowances: BenefitDesignMaxAllowances[], status: FamilyStatus) =>
  maxAllowances.find((x) => x.familyStatusId === status)?.maxAmount

const getStateNextAmount = (state: SetAllowancesProps, status: FamilyStatus) =>
  state.nextFamilyAllowances.find((x) => x.familyStatusId === status)?.amount

const fieldNameToStatus = (fieldName: string) =>
  fieldName === 'singleAmount'
    ? FamilyStatus.Single
    : fieldName === 'singleWithDependentsAmount'
    ? FamilyStatus.SingleWithDependents
    : fieldName === 'marriedAmount'
    ? FamilyStatus.Married
    : FamilyStatus.MarriedWithDependents

const SetAllowances = ({ bindSubmitFormHandler, state, ...props }: BaseComponentProps<SetAllowancesProps>) => {
  const { t: translate } = useTranslation()
  const { planPeriod } = useParams<any>()

  const { sendCommand } = useCommandRequestV2(setProductClassAllowances)

  const { data: maxAllowances, isLoading: maxAllowancesLoading } = useQueryRequestV2({
    initialState: [],
    queryFn: () => getBenefitDesignMaxAllowances(planPeriod),
  })

  const [initialValues, inputsMapping] = useMemo<[FormikValues, InputMappingObject[]]>(() => {
    const initialValues = {
      allowanceOptionId:
        state.offerSameAllowance === true
          ? AllowanceTypes.SameAllowance
          : state.offerSameAllowance === false
          ? AllowanceTypes.DifferentAllowance
          : null,
      offerMaxAllowance: state.offerMaxAllowance,
      sameAllowanceAmount: getStateAmount(state, FamilyStatus.Single),
      sameAllowanceMax: getMaxAmount(maxAllowances, FamilyStatus.Single),
      sameAllowanceNext: getStateNextAmount(state, FamilyStatus.Single),
      singleAmount: getStateAmount(state, FamilyStatus.Single),
      singleMax: getMaxAmount(maxAllowances, FamilyStatus.Single),
      singleNext: getStateNextAmount(state, FamilyStatus.Single),
      singleWithDependentsAmount: getStateAmount(state, FamilyStatus.SingleWithDependents),
      singleWithDependentsMax: getMaxAmount(maxAllowances, FamilyStatus.SingleWithDependents),
      singleWithDependentsNext: getStateNextAmount(state, FamilyStatus.SingleWithDependents),
      marriedAmount: getStateAmount(state, FamilyStatus.Married),
      marriedMax: getMaxAmount(maxAllowances, FamilyStatus.Married),
      marriedNext: getStateNextAmount(state, FamilyStatus.Married),
      marriedWithDependentsAmount: getStateAmount(state, FamilyStatus.MarriedWithDependents),
      marriedWithDependentsMax: getMaxAmount(maxAllowances, FamilyStatus.MarriedWithDependents),
      marriedWithDependentsNext: getStateNextAmount(state, FamilyStatus.MarriedWithDependents),
    }
    return [
      initialValues,
      [
        {
          status: FamilyStatus.Single,
          icon: 'fa-user',
          label: `Single allowance (up to ${formatMoney(initialValues.singleMax)})`,
          name: 'singleAmount',
          placeholder: 'i.e 300',
          nextAmount: initialValues.singleNext,
        },
        {
          status: FamilyStatus.SingleWithDependents,
          icon: 'fa-user-friends',
          label: `Single with dependents allowance (up to ${formatMoney(initialValues.singleWithDependentsMax)})`,
          name: 'singleWithDependentsAmount',
          placeholder: 'i.e 600',
          nextAmount: initialValues.singleWithDependentsNext,
        },
        {
          status: FamilyStatus.Married,
          icon: 'fa-user-friends',
          label: `Married allowance (up to ${formatMoney(initialValues.marriedMax)})`,
          name: 'marriedAmount',
          placeholder: 'i.e 600',
          nextAmount: initialValues.marriedNext,
        },
        {
          status: FamilyStatus.MarriedWithDependents,
          icon: 'fa-users',
          label: `Married with dependents allowance (up to ${formatMoney(initialValues.marriedWithDependentsMax)})`,
          name: 'marriedWithDependentsAmount',
          placeholder: 'i.e 600',
          nextAmount: initialValues.marriedWithDependentsNext,
        },
      ],
    ]
  }, [maxAllowances])

  useEffect(() => {
    props.setIsFormValid(formValidator(translate).isValidSync(initialValues))
  }, [initialValues])

  const onSubmit = async ({ allowanceOptionId, offerMaxAllowance, ...values }: FormikValues) => {
    const request: SetProductClassAllowanceRequest = {
      planPeriodId: planPeriod,
      productClassId: state.productClassId,
      offerSameAllowance: !!(allowanceOptionId === AllowanceTypes.SameAllowance),
      offerMaxAllowance: offerMaxAllowance,
      allowanceAmount:
        allowanceOptionId === AllowanceTypes.SameAllowance
          ? [{ familyStatusId: FamilyStatus.Single, amount: values.sameAllowanceAmount }]
          : Object.keys(values)
              .filter(
                (x) => x !== 'sameAllowanceAmount' && x !== 'undefined' && !x.includes('Max') && !x.includes('Next'),
              )
              .map((key) => ({ familyStatusId: fieldNameToStatus(key), amount: values[key] })),
    }
    const result = await sendCommand(request)
    result.completed ? props.goNext() : props.setIsSubmiting(false)
    props.setIsSubmiting(false)
  }

  console.log(state.saveAllowancesState)

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnChange={true}
      validate={(values) => {
        const isValid: boolean = formValidator(translate).isValidSync(values)
        props.setHasDirtyData(isValid)
        props.setIsFormValid(isValid)
        return {}
      }}
    >
      {({
        values: { allowanceOptionId, offerMaxAllowance, ...values },
        setFieldValue,
        setValues,
        handleSubmit,
        submitForm,
        ...formik
      }) => (
        bindSubmitFormHandler(submitForm),
        (
          <form autoComplete="off" onSubmit={handleSubmit} className="select_class">
            <h5 className="select_class_info mb-24">Set allowance amounts:</h5>

            {state.saveAllowancesState !== SaveAllowancesState.MidYear ? (
              <Radio.Group
                name="allowanceOptionId"
                className="qsehra-set-allowances qsehra-benefit-design-radio-group"
                onChange={() => {
                  formik.setErrors({})
                }}
              >
                {allowancesOptions.map((item, index) => (
                  <Radio.Button key={`${item.id}-${index}`} value={item.id} size="small" className="mb-8">
                    <p className="mb-0">{item.title}</p>
                  </Radio.Button>
                ))}
              </Radio.Group>
            ) : null}

            {allowanceOptionId !== null ? (
              <Skeleton loading={maxAllowancesLoading}>
                <div className="w-100 p-16 qsehra-allowances-inputs">
                  <Row className="">
                    <Col span={24}>
                      <h5 className="mb-24">
                        {translate('admin-dashboard.benefit-details.class-design.qsehra.set-allowances.allowances')}
                      </h5>
                    </Col>
                  </Row>
                  {allowanceOptionId === AllowanceTypes.SameAllowance &&
                  state.saveAllowancesState !== SaveAllowancesState.MidYear ? (
                    <>
                      <DefaultInput
                        label={`Monthly allowance amount (up to ${formatMoney(values.sameAllowanceMax)})`}
                        inputCustomStyle="mt-12"
                        name="sameAllowanceAmount"
                        disabled={offerMaxAllowance}
                        prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
                        placeholder={'0.00'}
                      />
                    </>
                  ) : (
                    inputsMapping.map(({ status, icon, label, name, placeholder, nextAmount }) => {
                      return (
                        <>
                          <Row key={status} className="mb-8">
                            <Col span={2} className="flex justify-center qsehra-input-icon-col">
                              <Icon className={`gray-6 fa ${icon}`} />
                            </Col>
                            <Col span={18} className={'mb-16'}>
                              <DefaultInput
                                label={label}
                                inputCustomStyle="mt-12"
                                name={name}
                                // disabled={offerMaxAllowance}
                                prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
                                placeholder={placeholder}
                              />
                              {state.saveAllowancesState === SaveAllowancesState.MidYear && nextAmount ? (
                                <p>
                                  <b>The allowance starting from the next month will be: {formatMoney(nextAmount)}</b>
                                </p>
                              ) : null}
                            </Col>
                          </Row>
                        </>
                      )
                    })
                  )}
                  {/* {state.saveAllowancesState === SaveAllowancesState.Renewal ? (
                    <Row>
                      {allowanceOptionId === AllowanceTypes.DifferentAllowance && <Col span={2} />}
                      <Col>
                        <CheckboxV2
                          name="offerMaxAllowance"
                          label={
                            allowanceOptionId === AllowanceTypes.SameAllowance
                              ? 'Adjust to maximum monthly allowance for 2024 when available.'
                              : `Adjust to maximum monthly allowance for all statuses for 2024 when available.`
                          }
                          onChange={(checked) =>
                            checked
                              ? allowanceOptionId === AllowanceTypes.SameAllowance
                                ? setFieldValue('sameAllowanceAmount', values.sameAllowanceMax)
                                : allowanceOptionId === AllowanceTypes.DifferentAllowance &&
                                  setValues({
                                    ...values,
                                    allowanceOptionId,
                                    offerMaxAllowance: checked,
                                    sameAllowanceAmount: values.sameAllowanceMax,
                                    singleAmount: values.singleMax,
                                    singleWithDependentsAmount: values.singleWithDependentsMax,
                                    marriedAmount: values.marriedMax,
                                    marriedWithDependentsAmount: values.marriedWithDependentsMax,
                                  })
                              : null
                          }
                        />
                      </Col>
                    </Row>
                  ) : null} */}
                </div>
              </Skeleton>
            ) : null}
          </form>
        )
      )}
    </Formik>
  )
}

const formValidator = (translate: any) =>
  Yup.object({
    allowanceOptionId: Yup.number().nullable().required(translate('general.required')),

    sameAllowanceAmount: Yup.number()
      .nullable()
      .typeError('Must be a number.')
      .when('allowanceOptionId', {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.SameAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(1, `${translate('general.min-value')}: $1`)
          .max(Yup.ref('sameAllowanceMax'), 'Must be lower than maximum allowance.')
          .required(translate('general.required')),
      }),

    singleAmount: Yup.number()
      .nullable()
      .when('allowanceOptionId', {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.DifferentAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(1, 'Must be greater than 0.')
          .max(Yup.ref('singleMax'), 'Must be lower than maximum allowance.')
          .required(translate('general.required')),
      }),

    singleWithDependentsAmount: Yup.number()
      .nullable()
      .when(['allowanceOptionId'], {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.DifferentAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(Yup.ref('singleAmount'), 'Must be greater than single allowance.')
          .max(Yup.ref('singleWithDependentsMax'), 'Must be lower than maximum allowance.')
          .required(translate('general.required')),
      }),

    marriedAmount: Yup.number()
      .nullable()
      .when('allowanceOptionId', {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.DifferentAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(Yup.ref('singleAmount'), 'Must be greater than single allowance.')
          .max(Yup.ref('marriedMax'), 'Must be lower than maximum allowance.')
          .required(translate('general.required')),
      }),

    marriedWithDependentsAmount: Yup.number()
      .nullable()
      .when('allowanceOptionId', {
        is: (allowanceOptionId: AllowanceTypes) => allowanceOptionId === AllowanceTypes.DifferentAllowance,
        then: Yup.number()
          .nullable()
          .typeError('Must be a number.')
          .min(Yup.ref('singleAmount'), 'Must be greater than single allowance.')
          .max(Yup.ref('marriedWithDependentsMax'), 'Must be lower than maximum allowance.')
          .required(translate('general.required')),
      }),
  })

export default SetAllowances
